import { useQuery } from '@tanstack/react-query';
import type { components as SyndicationComponents } from '@aaimplatform/institutional-types/generated/loan-syndication-protocol';
import { loanSyndicationClient } from '../api/typed-clients';
// Side-effect import registers the auth middleware on the typed clients.
import '../api/client';
import { formatApiError } from '../lib/api-error';

/**
 * The page size the list reads request. 100 is the spec maximum
 * (loan-syndication-protocol.openapi.yaml, parameter pageSize). The default
 * of 25 cut the lists at 25 rows with nothing on screen saying so; the pages
 * now read up to 100 and state it when the service holds more.
 */
export const SYNDICATION_LIST_PAGE_SIZE = 100;

/**
 * Participation offerings (GET /api/v1/syndication/offerings). The service
 * scopes this read to the caller's resolved tenant, which is the Aaim
 * platform tenant for an operator, so it is that tenant's own book and not
 * the network (loan-syndication-protocol.openapi.yaml, browseMarketplace).
 *
 * `status` is sent to the server. With no status the service answers its
 * default set for a non-lead caller (OPEN, ACTIVE, FULLY_COMMITTED), so a
 * filter applied in the browser over that list could never show a matured,
 * defaulted or canceled offering. A non-2xx response or a network failure
 * rejects, so the page shows the read failure instead of an empty list.
 */
export function useParticipationOfferings(
  status?: SyndicationComponents['schemas']['ParticipationOffering']['status'],
) {
  return useQuery({
    queryKey: ['syndication', 'offerings', SYNDICATION_LIST_PAGE_SIZE, status ?? 'current'],
    queryFn: async () => {
      const result = await loanSyndicationClient.GET(
        '/api/v1/syndication/offerings',
        {
          params: {
            query: status
              ? { pageSize: SYNDICATION_LIST_PAGE_SIZE, status }
              : { pageSize: SYNDICATION_LIST_PAGE_SIZE },
          },
        },
      );
      // Read before narrowing: the spec declares no error response for this
      // operation, so narrowing on `data` types the failure branch as never.
      const httpStatus = result.response.status;
      if (!result.data) {
        throw new Error(formatApiError(result.error, 'Request failed', httpStatus));
      }
      return result.data;
    },
  });
}

/**
 * Participation positions (GET /api/v1/syndication/positions). For an
 * operator the service returns the positions on offerings originated in the
 * Aaim platform tenant, not the network. Rejects on a non-2xx response or a
 * network failure.
 */
export function useParticipationPositions() {
  return useQuery({
    queryKey: ['syndication', 'positions', SYNDICATION_LIST_PAGE_SIZE],
    queryFn: async () => {
      const result = await loanSyndicationClient.GET(
        '/api/v1/syndication/positions',
        { params: { query: { pageSize: SYNDICATION_LIST_PAGE_SIZE } } },
      );
      // Read before narrowing: the spec declares no error response for this
      // operation, so narrowing on `data` types the failure branch as never.
      const status = result.response.status;
      if (!result.data) {
        throw new Error(formatApiError(result.error, 'Request failed', status));
      }
      return result.data;
    },
  });
}
